import { useMemo, useCallback } from 'react';
import { Card } from 'common/api/e-comm/models/Card';

export const wouldAdBeVisible = (
    brandId: string | number,
    walletAdSelector: (brandId: string | number) => any,
    dealSelector: (brandId: string | number) => any
): boolean => {
    const walletAdConfig = walletAdSelector(brandId);
    const cachedDeal = dealSelector(brandId);
    const hasPromotionalText = Boolean(walletAdConfig?.promotionalText);
    const hasCachedDeal = Boolean(cachedDeal);
    // undefined = deal not fetched yet (treat as still loading, show ad)
    // null = deal fetched but no results (don't show ad)
    // truthy = deal exists (show ad)
    const dealNotFetchedYet = cachedDeal === undefined;
    // Show ad if: has promo text, has cached deal, or (has wallet config AND deal not fetched yet)
    const dealStillLoading = walletAdConfig && dealNotFetchedYet;

    return hasPromotionalText || hasCachedDeal || dealStillLoading;
};

interface UseWalletAdDisplayLogicParams {
    sortedCards: Card[];
    walletAdBrandIds: string[];
    walletAdSelector: (brandId: string | number) => any;
    dealSelector: (brandId: string | number) => any;
    isActive?: boolean;
}

export const useWalletAdDisplayLogic = ({
    sortedCards,
    walletAdBrandIds,
    walletAdSelector,
    dealSelector,
    isActive = true,
}: UseWalletAdDisplayLogicParams): Set<string> => {
    const checkIfAdWouldBeVisible = useCallback(
        (brandId: string | number) => {
            return wouldAdBeVisible(brandId, walletAdSelector, dealSelector);
        },
        [walletAdSelector, dealSelector]
    );

    return useMemo(() => {
        const cardIdsToShowAds = new Set<string>();
        const brandIdsThatHaveShownAds = new Set<string | number>();

        if (!isActive || sortedCards.length === 0) {
            return cardIdsToShowAds;
        }

        let previousCardShowedVisibleAd = false;

        for (let i = 0; i < sortedCards.length; i++) {
            const card = sortedCards[i];
            const isCardLoading = (card as any).loading;
            const isSpecialCard = card.id === 'rokt-placement';

            if (isCardLoading || isSpecialCard) {
                previousCardShowedVisibleAd = false;
                continue;
            }

            const brandId = card.brandId;
            const brandIdAsString = String(brandId);
            const brandAlreadyShownAd = brandIdsThatHaveShownAds.has(brandId);
            const brandHasWalletAdConfig = walletAdBrandIds.includes(brandIdAsString);

            if (!brandHasWalletAdConfig) {
                previousCardShowedVisibleAd = false;
                continue;
            }

            if (brandAlreadyShownAd) {
                previousCardShowedVisibleAd = false;
                continue;
            }

            if (previousCardShowedVisibleAd) {
                previousCardShowedVisibleAd = false;
                continue;
            }

            const adWouldBeVisible = checkIfAdWouldBeVisible(brandId);
            if (adWouldBeVisible) {
                cardIdsToShowAds.add(card.id);
                brandIdsThatHaveShownAds.add(brandId);
                previousCardShowedVisibleAd = true;
            } else {
                previousCardShowedVisibleAd = false;
            }
        }

        return cardIdsToShowAds;
    }, [sortedCards, walletAdBrandIds, checkIfAdWouldBeVisible, isActive]);
};
